import React, { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../core/context/AuthContext";
import { initiateGoogleAuth } from "../../core/auth/google/googleAuth";
import LoggedInCTAButtons from "../../core/components/LoggedInCTAButtons";
import { GoogleIcon, ArrowRightIcon } from "../../design-system/icons";
import { SecondaryButton } from "../../design-system";
import { APP_DOMAIN } from "../../core/utils/config";
import { logoFontStyle } from "../../design-system/brand/logoFont";
import { submitPotentialCustomerLead } from "../../core/utils/leadsApi";
import { apiFetch } from "../../core/utils/api";
import { testimonialsData } from "../landing/testimonialsData";
import { UserProfileIcon, StarIcon } from "../../design-system/icons";

import LandingNavbar from "../landing/LandingNavbar";
import Footer from "../landing/Footer";
import useGlobalAccessGrantActive from "../landing/useGlobalAccessGrantActive";
import Website from "../../renderer/pages/Website";
import PreviewContext from "../../editor/context/PreviewContext";

import "./landing_v5.css";

const parseFeaturedWebsiteRoute = (route) => {
  const [username = "", domain = ""] = String(route || "")
    .split("/")
    .map((segment) => segment.trim())
    .filter(Boolean);

  return { username, domain };
};

const buildPreviewWebsite = (website, index) => {
  const { username, domain } = parseFeaturedWebsiteRoute(website?.url);
  if (!username || !domain) return null;

  return {
    ...website,
    previewKey: `${username}/${domain}-${index}`,
    username,
    domain,
    title: String(website?.name || "").trim() || `${username}/${domain}`,
  };
};

const LiveWebsitePreviewV5 = ({ website }) => {
  const previewContextValue = {
    mode: "responsive",
    viewport: null,
    containerWidth: 1440,
    contentWidth: 1440,
    onNavigate: () => {},
    showFrame: false,
    manualZoom: 1,
  };

  return (
    <div className="v5-iframe-wrapper">
      <PreviewContext.Provider value={previewContextValue}>
        <div style={{ width: "1440px", height: "1000px", transform: "scale(0.347)", transformOrigin: "top left" }}>
          <Website username={website.username} domain={website.domain} subdomain="" />
        </div>
      </PreviewContext.Provider>
    </div>
  );
};

const TYPEWRITER_PHRASES = [
  "Get hired faster by top companies.",
  "Impress MBA and Masters admissions.",
  "Showcase your academic work."
];

const TypewriterEffect = () => {
  const [text, setText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [loopNum, setLoopNum] = useState(0);
  const [typingSpeed, setTypingSpeed] = useState(80);

  useEffect(() => {
    let timer;
    const handleType = () => {
      const i = loopNum % TYPEWRITER_PHRASES.length;
    const fullText = TYPEWRITER_PHRASES[i];

      setText(isDeleting 
        ? fullText.substring(0, text.length - 1) 
        : fullText.substring(0, text.length + 1)
      );

      setTypingSpeed(isDeleting ? 30 : 70);

      if (!isDeleting && text === fullText) {
        timer = setTimeout(() => setIsDeleting(true), 2500);
      } else if (isDeleting && text === '') {
        setIsDeleting(false);
        setLoopNum(loopNum + 1);
        timer = setTimeout(() => {}, 500);
      } else {
        timer = setTimeout(handleType, typingSpeed);
      }
    };

    timer = setTimeout(handleType, typingSpeed);
    return () => clearTimeout(timer);
  }, [text, isDeleting, loopNum, typingSpeed]);

  return (
    <div className="flex items-center font-heading text-2xl sm:text-3xl font-bold h-12">
      <span className="mr-3 text-blue-500 text-3xl">❯</span>
      <span className="bg-gradient-to-r from-slate-700 to-slate-500 dark:from-dark-200 dark:to-dark-400 text-transparent bg-clip-text">
        {text}
      </span>
      <span className="animate-pulse border-r-2 border-blue-500 ml-1 h-10"></span>
    </div>
  );
};

const LandingHeroV5 = ({ isFreeGrantActive }) => {
  const [email, setEmail] = useState("");
  const { auth, isHydrated } = useContext(AuthContext);
  const navigate = useNavigate();
  const landingLeadSituation = "LANDING_EMAIL";
  
  const [websites, setWebsites] = useState([]);

  useEffect(() => {
    const fetchPublicWebsites = async () => {
      try {
        const response = await apiFetch("/api/websites/public/featured/");
        if (response.ok) {
          const data = await response.json();
          const parsed = (Array.isArray(data) ? data : [])
            .map((w, i) => buildPreviewWebsite(w, i))
            .filter(Boolean);
          setWebsites(parsed.slice(0, 4)); // Take top 4
        }
      } catch (err) {
        console.warn("Failed to fetch featured public websites", err);
      }
    };
    fetchPublicWebsites();
  }, []);

  const handleCreateWebsite = () => {
    const trimmedEmail = (email || "").trim();
    const isValid = trimmedEmail.length > 0 && trimmedEmail.includes("@");
    if (isValid) {
      void submitPotentialCustomerLead(
        { email: trimmedEmail, situation: landingLeadSituation },
        { keepalive: true }
      ).catch((err) => {
        console.error("Failed to submit potential customer lead:", err);
      });

      const base = `/edit/register?email=${encodeURIComponent(trimmedEmail)}`;
      navigate(base);
    }
  };

  const handleSocialLogin = () => {
    initiateGoogleAuth('register');
  };

  const isEmailValid = email.trim().length > 0 && email.includes("@");

  // Duplicate websites to create a seamless infinite scroll effect
  const scrollItems = [...websites, ...websites];

  return (
    <div className="landing-v5-container relative">
      {/* Aurora Background */}
      <div className="v5-aurora-bg">
        <div className="v5-aurora-blob v5-blob-1"></div>
        <div className="v5-aurora-blob v5-blob-2"></div>
        <div className="v5-aurora-blob v5-blob-3"></div>
        {/* Fade out gradient at the bottom to transition smoothly into the next section */}
        <div className="absolute bottom-0 left-0 w-full h-64 bg-gradient-to-t from-[#f8fafc] dark:from-dark-950 to-transparent z-10"></div>
      </div>

      <LandingNavbar />
      
      <div className="v5-hero-section">
        
        {/* Left Side: Text and Glassmorphism CTA */}
        <div className="v5-text-content">
          <div className="mb-4">
            <h1 className="text-6xl sm:text-7xl lg:text-8xl font-extrabold bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 text-transparent bg-clip-text pb-2" style={logoFontStyle}>
              {APP_DOMAIN}
            </h1>
          </div>
          
          <h2 className="text-sm sm:text-base lg:text-lg mb-6 leading-tight text-slate-800 dark:text-dark-200 transition-colors duration-300 whitespace-nowrap">
            Create a personal website that makes you <span className="italic font-semibold">impossible to ignore</span>
            {isFreeGrantActive ? (
              <span className="text-green-400 font-extrabold ml-1">{" — for free"}</span>
            ) : null}
          </h2>

          <div className="mb-10 transition-colors duration-300">
            <TypewriterEffect />
          </div>

          <div className="v5-glass-card">
            {!isHydrated ? (
              <div className="h-32" />
            ) : auth && auth.userId ? (
              <LoggedInCTAButtons username={auth.username} />
            ) : (
              <>
                <div className="mb-5">
                  <h3 className="font-heading text-xl font-bold text-slate-900 dark:text-white mb-1">Claim your custom domain</h3>
                  <p className="text-sm text-slate-500 dark:text-dark-400">Join thousands of professionals standing out online.</p>
                </div>
                <form
                  className="v5-input-wrapper"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!isEmailValid) return;
                    handleCreateWebsite();
                  }}
                >
                  <input
                    type="email"
                    aria-label="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email to start"
                    className="v5-input"
                  />
                  <button
                    type="submit"
                    disabled={!isEmailValid}
                    className="v5-submit-btn"
                    aria-label="Get started"
                  >
                    <ArrowRightIcon size="sm" />
                  </button>
                </form>

                <div className="flex items-center my-4">
                  <div className="flex-1 border-t border-slate-200 dark:border-dark-700"></div>
                  <span className="px-4 text-sm text-slate-400 dark:text-dark-500 uppercase tracking-widest">or</span>
                  <div className="flex-1 border-t border-slate-200 dark:border-dark-700"></div>
                </div>

                <SecondaryButton
                  type="button"
                  onClick={handleSocialLogin}
                  fullWidth={true}
                  leftIcon={<GoogleIcon size="sm" />}
                  variant="alwaysLight"
                  className="bg-white/50 dark:bg-dark-800/50 border-white/50 dark:border-dark-700 text-slate-900 dark:text-white hover:bg-white dark:hover:bg-dark-800 backdrop-blur-sm transition-all"
                >
                  Continue with Google
                </SecondaryButton>
              </>
            )}
          </div>
        </div>

        {/* Right Side: Isometric 3D Scroll */}
        <div className="v5-visual-content">
          <div className="v5-isometric-scene">
            <div className="v5-isometric-track">
              {scrollItems.length > 0 ? scrollItems.map((website, i) => (
                <div key={`${website.previewKey}-${i}`} className="v5-preview-card">
                  <LiveWebsitePreviewV5 website={website} />
                  <div className="v5-card-overlay">
                    <h3 className="v5-card-title">{website.title}</h3>
                    <p className="v5-card-domain">{website.domain}</p>
                  </div>
                </div>
              )) : (
                // Fallback loading cards
                [1, 2, 3, 4].map((i) => (
                  <div key={i} className="v5-preview-card flex items-center justify-center bg-slate-100 dark:bg-dark-800 animate-pulse">
                    <span className="text-slate-400 dark:text-dark-500">Loading preview...</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

      </div>

      <div className="relative z-10 transition-colors duration-300">
        <CreationStepsV5 />
        <TestimonialsV5 />
        <FinalCTAV5 isFreeGrantActive={isFreeGrantActive} />
        <Footer />
      </div>
    </div>
  );
};

const CreationStepsV5 = () => {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const steps = [
    {
      id: 1,
      title: "Upload your CV & Photo",
      color: "from-pink-500 to-rose-500",
    },
    {
      id: 2,
      title: "Choose Your Style",
      color: "from-purple-500 to-indigo-500",
    },
    {
      id: 3,
      title: "Wait for the AI Magic",
      color: "from-blue-500 to-cyan-500",
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white pb-2">
          Creation <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 text-transparent bg-clip-text">Reimagined</span>
        </h2>
      </div>

      <div className="grid lg:grid-cols-2 gap-12 items-center">
        {/* Left Column: Steps */}
        <div className="flex flex-col gap-6">
          {steps.map((step, index) => {
            const isActive = activeStep === index;
            return (
              <div
                key={step.id}
                onClick={() => setActiveStep(index)}
                className={`relative p-6 rounded-2xl cursor-pointer transition-all duration-500 overflow-hidden ${
                  isActive 
                    ? 'bg-white dark:bg-dark-800/80 shadow-2xl border border-slate-200 dark:border-dark-700 transform scale-105 z-10' 
                    : 'hover:bg-white/50 dark:hover:bg-dark-800/50 border border-transparent opacity-70 hover:opacity-100'
                }`}
              >
                {/* Progress Bar */}
                {isActive && (
                  <div 
                    key={`progress-${activeStep}`} 
                    className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r ${step.color} animate-[progress_5s_linear_forwards]`}
                  ></div>
                )}

                <div className="flex items-center gap-5">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 text-xl font-black transition-all duration-500 ${
                    isActive 
                      ? `bg-gradient-to-br ${step.color} text-white shadow-lg scale-110` 
                      : 'bg-slate-100 dark:bg-dark-800 text-slate-400 dark:text-dark-500'
                  }`}>
                    {step.id}
                  </div>
                  <h3 className={`font-heading text-2xl font-bold transition-colors duration-500 ${
                    isActive ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-dark-400'
                  }`}>
                    {step.title}
                  </h3>
                </div>
              </div>
            );
          })}
        </div>

        {/* Right Column: Interactive Stage */}
        <div className="relative w-full h-[350px] md:h-[450px] rounded-[2rem] bg-gradient-to-br from-slate-100 to-slate-200 dark:from-dark-800 dark:to-dark-900 border border-slate-200 dark:border-dark-700 overflow-hidden shadow-2xl flex items-center justify-center">
          
          {/* Background Ambient Gradients */}
          <div className="absolute inset-0 opacity-30 dark:opacity-20">
            <div className={`absolute top-0 left-0 w-full h-full bg-gradient-to-br from-pink-500 to-transparent transition-opacity duration-1000 ${activeStep === 0 ? 'opacity-100' : 'opacity-0'}`}></div>
            <div className={`absolute top-0 left-0 w-full h-full bg-gradient-to-br from-purple-500 to-transparent transition-opacity duration-1000 ${activeStep === 1 ? 'opacity-100' : 'opacity-0'}`}></div>
            <div className={`absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-500 to-transparent transition-opacity duration-1000 ${activeStep === 2 ? 'opacity-100' : 'opacity-0'}`}></div>
          </div>

          {/* Stage 1: Upload & Extract */}
          <div className={`absolute inset-0 flex items-center justify-center transition-all duration-700 transform ${activeStep === 0 ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-95 z-0 pointer-events-none'}`}>
            {/* Dashed Dropzone */}
            <div className="absolute inset-8 border-2 border-dashed border-pink-500/30 rounded-3xl animate-[pulse_4s_ease-in-out_infinite]"></div>
            
            {/* AI Analysis Badge */}
            <div className="absolute top-12 left-1/2 -translate-x-1/2 bg-dark-900/80 dark:bg-dark-50/90 backdrop-blur-md px-4 py-1.5 rounded-full shadow-xl border border-slate-700 dark:border-dark-200 z-40 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-pink-500 animate-ping"></div>
              <span className="text-xs font-bold tracking-widest uppercase text-white dark:text-dark-900">AI Analysis</span>
            </div>

            <div className="relative flex items-center justify-center w-full h-full">
              {/* CV Card */}
              <div className="absolute w-40 h-56 bg-white dark:bg-dark-800 rounded-xl shadow-2xl border border-slate-200 dark:border-dark-700 p-4 overflow-hidden animate-[float_3s_ease-in-out_infinite] z-10 -ml-16">
                <div className="w-10 h-10 rounded-full bg-pink-100 dark:bg-pink-900/30 mb-4"></div>
                <div className="w-full h-2 bg-slate-100 dark:bg-dark-700 rounded mb-3"></div>
                <div className="w-5/6 h-2 bg-slate-100 dark:bg-dark-700 rounded mb-3"></div>
                <div className="w-4/6 h-2 bg-slate-100 dark:bg-dark-700 rounded mb-6"></div>
                <div className="w-full h-2 bg-slate-100 dark:bg-dark-700 rounded mb-3"></div>
                <div className="w-3/4 h-2 bg-slate-100 dark:bg-dark-700 rounded"></div>
              </div>

              {/* Image Card */}
              <div className="absolute w-32 h-32 bg-white dark:bg-dark-800 rounded-xl shadow-2xl border border-slate-200 dark:border-dark-700 p-2 overflow-hidden animate-[float_3.5s_ease-in-out_infinite] z-20 ml-20 mt-16 flex items-center justify-center">
                <div className="w-full h-full bg-slate-100 dark:bg-dark-700 rounded-lg flex items-center justify-center">
                  <svg className="w-12 h-12 text-slate-300 dark:text-dark-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>

              {/* Extracted Data Tags (Animated) */}
              <div className="absolute z-30 flex flex-col gap-2 -ml-32 mt-10">
                <div className="bg-pink-500 text-white text-[10px] font-bold px-2 py-1 rounded shadow-lg opacity-0 animate-[extract_4s_ease-in-out_infinite]">Software Engineer</div>
                <div className="bg-pink-500 text-white text-[10px] font-bold px-2 py-1 rounded shadow-lg opacity-0 animate-[extract_4s_ease-in-out_infinite_0.5s]">Harvard University</div>
                <div className="bg-pink-500 text-white text-[10px] font-bold px-2 py-1 rounded shadow-lg opacity-0 animate-[extract_4s_ease-in-out_infinite_1s]">5+ Years Exp</div>
              </div>

              {/* Scanner Line */}
              <div className="absolute w-72 h-1 bg-pink-500 shadow-[0_0_20px_rgba(236,72,153,1)] animate-[scan_3s_ease-in-out_infinite] z-30"></div>
            </div>
          </div>

          {/* Stage 2: Customize */}
          <div className={`absolute inset-0 flex items-center justify-center transition-all duration-700 transform ${activeStep === 1 ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-95 z-0 pointer-events-none'}`}>
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Palette Card */}
              <div className="absolute top-1/4 left-[10%] sm:left-[15%] w-32 bg-white/90 dark:bg-dark-800/90 backdrop-blur rounded-2xl shadow-xl border border-slate-200 dark:border-dark-700 p-4 animate-[float_3s_ease-in-out_infinite]">
                <div className="text-xs font-bold text-slate-500 mb-3">Colors</div>
                <div className="flex justify-between relative">
                  <div className="w-6 h-6 rounded-full bg-pink-500 shadow-lg relative">
                    <div className="absolute -inset-1 rounded-full border-2 border-pink-500 animate-[fade-ring-1_6s_infinite]"></div>
                  </div>
                  <div className="w-6 h-6 rounded-full bg-purple-500 shadow-lg relative">
                    <div className="absolute -inset-1 rounded-full border-2 border-purple-500 animate-[fade-ring-2_6s_infinite]"></div>
                  </div>
                  <div className="w-6 h-6 rounded-full bg-blue-500 shadow-lg relative">
                    <div className="absolute -inset-1 rounded-full border-2 border-blue-500 animate-[fade-ring-3_6s_infinite]"></div>
                  </div>
                </div>
              </div>
              
              {/* Typography Card */}
              <div className="absolute bottom-1/4 right-[10%] sm:right-[15%] w-32 bg-white/90 dark:bg-dark-800/90 backdrop-blur rounded-2xl shadow-xl border border-slate-200 dark:border-dark-700 p-4 animate-[float_4s_ease-in-out_infinite_reverse]">
                <div className="text-xs font-bold text-slate-500 mb-2">Typography</div>
                <div className="text-3xl font-serif font-bold text-slate-800 dark:text-white animate-[font-change_4s_infinite]">Aa</div>
              </div>

              {/* Tone Card */}
              <div className="absolute top-1/3 right-[5%] sm:right-[10%] bg-white/90 dark:bg-dark-800/90 backdrop-blur rounded-xl shadow-xl border border-slate-200 dark:border-dark-700 px-4 py-2 animate-[float_3.5s_ease-in-out_infinite]">
                <div className="text-xs font-bold text-purple-500">Tone: Professional</div>
              </div>
              
              {/* Central Wireframe changing */}
              <div className="w-48 h-48 bg-white dark:bg-dark-800 rounded-[2rem] shadow-2xl border-4 border-purple-500 p-4 flex flex-col gap-3 animate-[border-color-change_6s_infinite]">
                <div className="w-full h-1/2 rounded-xl animate-[bg-color-change_6s_infinite]"></div>
                <div className="w-3/4 h-4 bg-slate-100 dark:bg-dark-700 rounded-full"></div>
                <div className="w-1/2 h-4 bg-slate-100 dark:bg-dark-700 rounded-full"></div>
              </div>
            </div>
          </div>

          {/* Stage 3: Magic */}
          <div className={`absolute inset-0 flex items-center justify-center transition-all duration-700 transform ${activeStep === 2 ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-95 z-0 pointer-events-none'}`}>
            
            {/* Generating Overlay Animation */}
            {activeStep === 2 && (
              <div className="absolute inset-0 bg-dark-900/40 backdrop-blur-md z-50 flex flex-col items-center justify-center animate-[fade-out-overlay_1.5s_ease-in-out_forwards] rounded-[2rem]">
                <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                <div className="text-white font-bold tracking-widest uppercase text-sm animate-pulse">Generating AI...</div>
              </div>
            )}

            {/* Mini Browser */}
            <div className="relative w-64 h-80 bg-white dark:bg-dark-900 rounded-xl shadow-2xl border border-slate-200 dark:border-dark-700 overflow-hidden flex flex-col animate-[float_4s_ease-in-out_infinite]">
              {/* Browser header */}
              <div className="h-8 bg-slate-100 dark:bg-dark-800 flex items-center px-3 gap-1.5 border-b border-slate-200 dark:border-dark-700">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
                <div className="ml-4 flex-1 h-4 bg-white dark:bg-dark-900 rounded text-[8px] text-center text-slate-400 flex items-center justify-center font-mono">myvitae.ai/you</div>
              </div>
              {/* Website content */}
              <div className="p-4 flex-1 flex flex-col gap-3 relative">
                <div className="w-full h-24 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 rounded-lg shadow-inner flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center text-white">
                    <UserProfileIcon size="lg" />
                  </div>
                </div>
                <div className="w-2/3 h-4 bg-slate-200 dark:bg-dark-700 rounded"></div>
                <div className="w-full h-2 bg-slate-200 dark:bg-dark-700 rounded"></div>
                <div className="w-5/6 h-2 bg-slate-200 dark:bg-dark-700 rounded mb-2"></div>
                
                {/* Message Me Button */}
                <div className="w-24 h-6 bg-blue-500 rounded-full flex items-center justify-center text-[8px] font-bold text-white shadow-md animate-[button-click_5s_ease-in-out_forwards]" id="mockup-btn">
                  Message Me
                </div>
                
                {/* Success Overlay */}
                <div key={`success-${activeStep}`} className="absolute inset-0 bg-white/50 dark:bg-dark-900/50 backdrop-blur-sm flex items-center justify-center opacity-0 animate-[fade-in_0.5s_ease-out_3s_forwards] pointer-events-none">
                  <div className="bg-green-500 text-white px-4 py-2 rounded-full font-bold shadow-lg flex items-center gap-2 transform scale-0 animate-[pop-in_0.5s_ease-out_3s_forwards]">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                    Live
                  </div>
                </div>
              </div>
            </div>

            {/* Animated Cursor */}
            {activeStep === 2 && (
              <div className="absolute z-40 w-6 h-6 text-slate-800 dark:text-white animate-[cursor-move_5s_ease-in-out_forwards]" style={{ filter: 'drop-shadow(0 4px 3px rgb(0 0 0 / 0.3))', left: 'calc(50% + 20px)', top: 'calc(50% + 10px)' }}>
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
                  <path d="M7 2l12 11.2-5.8.5 3.3 7.3-2.2.9-3.2-7.4-4.4 4.7z" />
                </svg>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom animations */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
        @keyframes scan {
          0%, 100% { top: 15%; opacity: 0; }
          10%, 90% { opacity: 1; }
          50% { top: 85%; }
        }
        @keyframes extract {
          0% { transform: translateX(-20px); opacity: 0; }
          20%, 80% { transform: translateX(0); opacity: 1; }
          100% { transform: translateX(20px); opacity: 0; }
        }
        @keyframes pulse-scale {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); box-shadow: 0 0 15px rgba(0,0,0,0.2); }
        }
        @keyframes font-change {
          0%, 100% { font-family: serif; }
          33% { font-family: sans-serif; font-style: italic; }
          66% { font-family: monospace; }
        }
        @keyframes bg-color-change {
          0%, 100% { background-color: rgba(236, 72, 153, 0.2); } /* pink */
          33% { background-color: rgba(168, 85, 247, 0.2); } /* purple */
          66% { background-color: rgba(59, 130, 246, 0.2); } /* blue */
        }
        @keyframes border-color-change {
          0%, 100% { border-color: rgba(236, 72, 153, 0.8); } /* pink */
          33% { border-color: rgba(168, 85, 247, 0.8); } /* purple */
          66% { border-color: rgba(59, 130, 246, 0.8); } /* blue */
        }
        @keyframes text-color-change {
          0%, 100% { color: rgb(236, 72, 153); } /* pink */
          33% { color: rgb(168, 85, 247); } /* purple */
          66% { color: rgb(59, 130, 246); } /* blue */
        }
        @keyframes fade-ring-1 {
          0%, 25%, 100% { opacity: 1; transform: scale(1); }
          33%, 90% { opacity: 0; transform: scale(0.8); }
        }
        @keyframes fade-ring-2 {
          0%, 25%, 66%, 100% { opacity: 0; transform: scale(0.8); }
          33%, 58% { opacity: 1; transform: scale(1); }
        }
        @keyframes fade-ring-3 {
          0%, 58%, 100% { opacity: 0; transform: scale(0.8); }
          66%, 90% { opacity: 1; transform: scale(1); }
        }
        @keyframes cursor-move {
          0% { transform: translate(100px, 100px); opacity: 0; }
          10% { opacity: 1; }
          40% { transform: translate(-45px, 60px); }
          50% { transform: translate(-45px, 60px) scale(0.9); } /* Click down */
          60% { transform: translate(-45px, 60px) scale(1); } /* Click up */
          90% { opacity: 1; }
          100% { transform: translate(100px, 100px); opacity: 0; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }
        @keyframes fade-in {
          to { opacity: 1; }
        }
        @keyframes pop-in {
          to { transform: scale(1); }
        }
        @keyframes fade-out-overlay {
          0%, 60% { opacity: 1; visibility: visible; }
          100% { opacity: 0; visibility: hidden; }
        }
        @keyframes button-click {
          0%, 45% { transform: scale(1); background-color: #3b82f6; }
          50% { transform: scale(1.1); background-color: #60a5fa; box-shadow: 0 0 15px rgba(59, 130, 246, 0.5); }
          55%, 100% { transform: scale(1); background-color: #3b82f6; }
        }
      `}} />
    </div>
  );
};

const TestimonialsV5 = () => {
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <StarIcon key={index} size="sm" className="text-yellow-400" filled />
    ));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-center mb-8">
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white pb-2">
          What Our <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 text-transparent bg-clip-text">Users Say</span>
        </h2>
        <p className="text-lg sm:text-xl text-slate-600 dark:text-dark-300 mt-2">
          Join thousands of professionals who trust {APP_DOMAIN}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {testimonialsData.map((testimonial) => (
          <div 
            key={testimonial.id}
            className="bg-white/50 dark:bg-dark-800/50 backdrop-blur-md border border-slate-200 dark:border-dark-700 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300"
          >
            <div className="flex items-center mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-pink-100 to-blue-100 dark:from-dark-700 dark:to-dark-600 rounded-full flex items-center justify-center mr-4 shadow-inner">
                <UserProfileIcon size="lg" className="text-purple-500 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-slate-900 dark:text-white">
                  {testimonial.name}
                </h3>
                <p className="text-sm text-slate-500 dark:text-dark-400">
                  {testimonial.profession}
                </p>
              </div>
            </div>

            <div className="flex items-center mb-4">
              {renderStars(testimonial.rating)}
            </div>

            <p className="text-slate-700 dark:text-dark-300 leading-relaxed">
              "{testimonial.feedback}"
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const FinalCTAV5 = ({ isFreeGrantActive }) => {
  const [email, setEmail] = useState("");
  const { auth, isHydrated } = useContext(AuthContext);
  const navigate = useNavigate();
  const landingLeadSituation = "LANDING_EMAIL";

  const handleCreateWebsite = () => {
    const trimmedEmail = (email || "").trim();
    const isValid = trimmedEmail.length > 0 && trimmedEmail.includes("@");
    if (isValid) {
      void submitPotentialCustomerLead(
        { email: trimmedEmail, situation: landingLeadSituation },
        { keepalive: true }
      ).catch((err) => {
        console.error("Failed to submit potential customer lead:", err);
      });

      const base = `/edit/register?email=${encodeURIComponent(trimmedEmail)}`;
      navigate(base);
    }
  };

  const handleSocialLogin = () => {
    initiateGoogleAuth('register');
  };

  const isEmailValid = email.trim().length > 0 && email.includes("@");

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
      <div className="v5-glass-card p-8 sm:p-12 mt-0">
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white pb-2 mb-4">
          Ready to create your <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 text-transparent bg-clip-text">{isFreeGrantActive ? "free " : ""}professional</span> website?
        </h2>
        
        <div className="max-w-md mx-auto mt-10">
          {!isHydrated ? (
            <div className="h-32" />
          ) : auth && auth.userId ? (
            <LoggedInCTAButtons username={auth.username} />
          ) : (
            <>
              <div className="mb-6">
                <h3 className="font-heading text-xl font-bold text-slate-900 dark:text-white mb-2">Claim your custom domain</h3>
                <p className="text-slate-500 dark:text-dark-400">Join thousands of professionals standing out online.</p>
              </div>
              <form
                className="v5-input-wrapper mb-6"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!isEmailValid) return;
                  handleCreateWebsite();
                }}
              >
                  <input
                    type="email"
                    aria-label="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email to start"
                    className="v5-input"
                  />
                <button
                  type="submit"
                  disabled={!isEmailValid}
                  className="v5-submit-btn"
                  aria-label="Get started"
                >
                  <ArrowRightIcon size="sm" />
                </button>
              </form>

              <div className="flex items-center my-6">
                <div className="flex-1 border-t border-slate-200 dark:border-dark-700"></div>
                <span className="px-4 text-sm text-slate-400 dark:text-dark-500 uppercase tracking-widest">or</span>
                <div className="flex-1 border-t border-slate-200 dark:border-dark-700"></div>
              </div>

              <SecondaryButton
                type="button"
                onClick={handleSocialLogin}
                fullWidth={true}
                leftIcon={<GoogleIcon size="sm" />}
                variant="alwaysLight"
                className="bg-white/50 dark:bg-dark-800/50 border-white/50 dark:border-dark-700 text-slate-900 dark:text-white hover:bg-white dark:hover:bg-dark-800 backdrop-blur-sm transition-all py-3"
              >
                Sign up with Google
              </SecondaryButton>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const LandingV5 = () => {
  const isFreeGrantActive = useGlobalAccessGrantActive();

  return <LandingHeroV5 isFreeGrantActive={isFreeGrantActive} />;
};

export default LandingV5;
